import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";

export default function VerifyResetPage() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  const { showToast, ToastContainer } = useToast();
  const navigate = useNavigate();
  const email = useLocation().state?.email; // received from ForgotPasswordPage

  // Countdown for resend button
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Verify code
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email || !code) return showToast("Please enter the 6-digit code.", "error");

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/verify-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Verification failed.");

      // Navigate immediately once verified
      navigate("/reset-password", { state: { email } });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const res = await fetch(`${API_BASE}/api/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await res.json();
      showToast(`A new reset code has been sent to ${email}.`, "success");
      setCooldown(60);
    } catch {
      showToast("Failed to resend code.", "error");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full flex-grow flex flex-col items-center justify-center py-16">
      <div className="bg-white rounded-xl shadow p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold text-center mb-6 text-gray-800">
          Verify Reset Code
        </h1>

        <form onSubmit={handleVerify} className="space-y-4">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full border rounded-lg px-3 py-2 text-center tracking-widest font-semibold placeholder:italic placeholder:text-gray-400"
            maxLength={6}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Verify Code"}
          </button>

          <div className="text-center mt-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0 || resending}
              className={`text-sm ${
                cooldown > 0 || resending
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-blue-600 hover:underline"
              }`}
            >
              {cooldown > 0
                ? `Resend code in ${cooldown}s`
                : resending
                ? "Sending..."
                : "Didn’t receive code? Resend"}
            </button>
          </div>
        </form>
      </div>

      <LoadingOverlay show={loading || resending} text="Loading..." />
      <ToastContainer />
    </div>
  );
}
